// src/pages/DiscountsPage.jsx
import { useEffect, useMemo, useState } from 'react';
import DiscountsTab from '../components/DiscountsTab';
import { deleteDiscount, getDiscounts, saveDiscount, getBaseCurrency } from '../utils/api';
import { validateAll, couponUniqErrors } from '../utils/discountValidation';

export default function DiscountsPage() {
  const businessId = getBusinessIdFromRouterState();

  const [activeTab, setActiveTab] = useState('reservations');
  const [reservations, setReservations] = useState({ items: [], loaded: false, errors: {} });
  const [products, setProducts] = useState({ items: [], loaded: false, errors: {} });
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [currencyMeta, setCurrencyMeta] = useState({ code: 'USD', digits: 2, symbol: '$', sample: '$1,234.56' });
  // initial load
  useEffect(() => {
    let mounted = true;
    async function load(kind) {
      try {
        const server = await getDiscounts(kind, businessId);
        if (!mounted) return;
        if (kind === 'reservations') {
          setReservations({ items: server ?? [], loaded: true, errors: {} });
        } else {
          setProducts({ items: server ?? [], loaded: true, errors: {} });
        }
      } catch (e) {
        console.error(e);
        if (mounted) setLoadError(e.message || 'Failed to load');
      }
    }
    async function loadCurrency() {
      try {
        const code = await getBaseCurrency(businessId);
        if (!mounted) return;
        setCurrencyMeta(deriveCurrencyMeta(code || 'USD'));
      } catch {
        if (!mounted) setCurrencyMeta(deriveCurrencyMeta('USD'));
      }
    }
    setReservations(s => ({ ...s, loaded: false }));
    setProducts(s => ({ ...s, loaded: false }));
    load('reservations');
    loadCurrency();
    load('products');
    return () => { mounted = false; };
  }, [businessId]);


  // LIVE VALIDATION any time either list changes
  useEffect(() => {
    if (!reservations.loaded && !products.loaded) return;

   const resField = validateAll(reservations.items, 'reservations', { currencyDigits: currencyMeta.digits });
   const prodField = validateAll(products.items, 'products',       { currencyDigits: currencyMeta.digits });

    const uniq = couponUniqErrors(reservations.items, products.items);

    setReservations(s => ({ ...s, errors: mergeErrors(resField, uniq.reservations) }));
    setProducts(s => ({ ...s, errors: mergeErrors(prodField, uniq.products) }));
  }, [reservations.items, products.items, reservations.loaded, products.loaded, currencyMeta.digits]);

  const { items, setItems, errors } = useMemo(() => {
    return activeTab === 'reservations'
      ? { items: reservations.items, setItems: (fn) => setReservations(s => ({ ...s, items: typeof fn === 'function' ? fn(s.items) : fn })), errors: reservations.errors }
      : { items: products.items, setItems: (fn) => setProducts(s => ({ ...s, items: typeof fn === 'function' ? fn(s.items) : fn })), errors: products.errors };
  }, [activeTab, reservations, products]);

  async function handleSave() {
    setBusy(true);
    const kind = activeTab;
    try {
      // field errors already live; block save if any errors on active tab
      const hasErrors = Object.keys(kind === 'reservations' ? reservations.errors : products.errors).length > 0;
      if (hasErrors) {
        setBusy(false);
        return;
      }
      for (const d of items) {
        if (!d._dirty) continue;
        const payload = serializeForApi(d);
        await saveDiscount(kind, payload, d.id, businessId);
      }
      const fresh = await getDiscounts(kind, businessId);
      if (kind === 'reservations') setReservations({ items: fresh, loaded: true, errors: {} });
      else setProducts({ items: fresh, loaded: true, errors: {} });
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  }
// Helper: derive digits/symbol safely from Intl (no extra imports)
// Helper: derive digits/symbol safely from Intl (no extra imports)
function deriveCurrencyMeta(code) {
  const defaultMeta = { code, digits: 2, symbol: code, sample: `${code} 1234.56` };
  try {
    const nf = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: code,
      currencyDisplay: 'narrowSymbol',
    });
    const parts = nf.formatToParts(1);
    const symbol = parts.find(p => p.type === 'currency')?.value || code;
    const digits = nf.resolvedOptions().maximumFractionDigits;
    return { code, digits, symbol, sample: nf.format(1234.56) };
  } catch {
    return defaultMeta;
  }
}


  async function handleDelete(d, idx) {
    if (d.id) {
      try { await deleteDiscount(activeTab, d.id, businessId); } catch (e) { console.error(e); }
    }
    setItems(prev => prev.filter((_, i) => i !== idx));
  }
const hasDirty = (activeTab === 'reservations'
  ? reservations.items
  : products.items
).some(d => d._dirty);

  return (
    <div className="mx-auto max-w-4xl p-6 pb-28">
      <h1 className="mb-6 text-2xl font-bold">Discounts</h1>

      {/* Tabs */}
      <div className="mb-4 border-b border-gray-200">
        <div className="-mb-px flex gap-4">
          {['reservations', 'products'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`whitespace-nowrap border-b-2 pb-2 text-sm font-medium ${
                activeTab === tab
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab === 'reservations' ? 'Reservations' : 'Products'}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      {loadError ? (
        <p className="text-red-600">{loadError}</p>
      ) : activeTab === 'reservations' ? (
        <DiscountsTab
          kind="reservations"
          businessId={businessId}
          items={reservations.items}
          setItems={fn => setReservations(s => ({ ...s, items: typeof fn === 'function' ? fn(s.items) : fn }))}
          errorsByIndex={reservations.errors}
          onDelete={handleDelete}
          currencyMeta={currencyMeta}
        />
      ) : (
        <DiscountsTab
          kind="products"
          businessId={businessId}
          items={products.items}
          setItems={fn => setProducts(s => ({ ...s, items: typeof fn === 'function' ? fn(s.items) : fn }))}
          errorsByIndex={products.errors}
          onDelete={handleDelete}
          currencyMeta={currencyMeta}
        />
      )}

      {/* Sticky Save */}
      <div className="fixed inset-x-0 bottom-0 z-10 border-t bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/70">
        <div className="mx-auto flex max-w-4xl items-center justify-between gap-4 p-3">
          <div className="text-sm text-gray-600">
            {activeTab === 'reservations' ? 'Reservations discounts' : 'Product discounts'} — changes are saved with the button.
          </div>
          <button
            onClick={handleSave}
            disabled={busy || !hasDirty}
            className="rounded bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {busy ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* helpers */
function getBusinessIdFromRouterState() {
  try {
    const loc = window.history.state?.usr;
    return loc?.currentBusinessId ?? null;
  } catch { return null; }
}
function mergeServerAndDrafts(server, drafts) {
  if (!drafts) return server ?? [];
  const merged = [];
  drafts.forEach(d => { if (!d.id) merged.push(d); });
  (server ?? []).forEach(s => {
    const draft = drafts.find(d => d.id && d.id === s.id);
    merged.push(draft ? { ...s, ...draft } : s);
  });
  return merged;
}
function mergeErrors(a, b) {
  const out = { ...a };
  for (const [k, v] of Object.entries(b || {})) {
    out[k] = { ...(out[k] || {}), ...v };
  }
  return out;
}
function serializeForApi(d) {
  const toDate = iso => (iso ? new Date(iso) : null);
  const fmt = dt => (dt ? dt.toISOString().slice(0, 10) : null);
  const start = fmt(toDate(d.start_date));
  const end   = fmt(toDate(d.end_date));
  return {
    ...d,
    start_date: start,
    end_date: end,
    items: d.apply_all ? [] : (d.items || []),
    // keep new fields; legacy single-value fields omitted
    free_product_id: null,
    discounted_product_id: null,
    discounted_product_percent: 0,
  };
}
